// component/report/NewbieDetail.jsx
/**
 * NewbieDetail (신규 가입자 리스트 테이블)
 * --------------------------------------------------------------------
 * 목적:
 *  - 선택된 기간(page)의 신규 가입자 목록을 표로 표시.
 *  - 각 행의 "조회" 버튼으로 해당 유저의 포인트 상세 모달을 띄우도록 상위 콜백 호출.
 *
 * 데이터 계약(가정):
 *  - props.visitDetailValue[page] = Array<{
 *      name: string,
 *      total_count: number,  // 방문 수(또는 이용 횟수)
 *      total_point: number,  // 포인트 합산
 *      phone_number?: string // 모달 조회용(상위에서 사용)
 *    }>
 *
 */

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

function NewbieDetail(props) {
  const [newbieArray, setNewbieArray] = useState([]);

  // 테이블 공통 셀 스타일
  const baseCell =
    "flex items-center justify-center border-b border-solid border-neutral-500";

  // 컬럼 정의(너비/라벨)
  const cols = [
    { w: "w-[10%]", label: props.t("newbieDetail.index") }, // 번호
    { w: "w-[20%]", label: props.t("newbieDetail.userName") }, // 이름
    { w: "w-[20%]", label: props.t("newbieDetail.visits") }, // 방문 수
    { w: "w-[25%]", label: props.t("newbieDetail.points") }, // 포인트
    { w: "w-[25%]", label: props.t("newbieDetail.credit") }, // 조회 버튼
  ];

  // 현재 페이지 데이터 반영: 방어적 복사로 상태에 저장
  useEffect(() => {
    if (props.visitDetailValue && props.visitDetailValue[props.page]) {
      setNewbieArray([...props.visitDetailValue[props.page]]);
    } else {
      setNewbieArray([]);
    }
  }, [props.visitDetailValue, props.page]);

  // 방문 수 0 제외(노이즈 제거). 필드명 total_count 가정
  const filtered = newbieArray.filter((e) => e.total_count !== 0);

  return (
    <motion.div
      initial={{ opacity: 0 }} // 페이드 인 초기값
      animate={{ opacity: 1 }} // 페이드 인
      transition={{ duration: 0.5 }} // 전환 시간
      className="block w-[100%] px-[5%] py-10 border border-[#f3f4f6]"
      id="newbie-detail"
    >
      {/* 상단 타이틀/뒤로가기 */}
      <div className="flex items-center justify-center relative h-[50px] w-full">
        <div
          className="absolute left-2 text-[5vw] font-bold cursor-pointer"
          onClick={props.goBack} // navigate(-1)
        >
          &lt;
        </div>
        <div className="text-[5vw] font-bold text-center">
          {props.t(`newbieDetail.${props.page}`)} {props.t("newbieDetail.back")}
        </div>
      </div>

      {/* 테이블 */}
      <div className="mb-8">
        {/* 헤더 */}
        <div className="flex w-full h-10 text-[3.4vw] font-medium text-neutral-500">
          {cols.map(({ w, label }) => (
            <div key={label} className={`${w} ${baseCell}`}>
              {label}
            </div>
          ))}
        </div>

        {/* 바디 */}
        {filtered.length > 0 ? (
          filtered.map((element, index) => (
            <div
              className="best-rank__each"
              key={index}
              id={index % 2 === 0 ? "even" : "odd"}
            >
              {/* 번호 */}
              <div className="flex w-[10%] items-center justify-center text-[3.8vw] font-extrabold text-left">
                {`${index + 1}.`}
              </div>

              {/* 사용자 이름(길면 말줄임) */}
              <div
                className="flex w-[20%] items-center justify-center text-[3.8vw] font-medium text-left truncate"
                title={element.name}
              >
                {element.name.length > 4
                  ? `${element.name.slice(0, 3)}...`
                  : element.name}
              </div>

              {/* 방문 수 */}
              <div className="flex w-[20%] items-center justify-center text-[4vw] font-bold text-left">
                {element.total_count}회
              </div>

              {/* 포인트 합계(천단위 구분자) */}
              <div className="flex w-[25%] items-center justify-center text-[4vw] font-bold text-left">
                {element.total_point.toLocaleString()}p
              </div>

              {/* 상세 조회 버튼 */}
              <div
                className="flex w-[25%] items-center justify-center text-[3.5vw] font-bold text-left"
                onClick={() => props.onView(element)}
              >
                <button
                  className="flex rounded-xl w-[50%] py-[2px] h-[30%] items-center justify-center text-white font-bold bg-violet-400"
                  onClick={() => props.onView(element)}
                >
                  조회
                </button>
              </div>
            </div>
          ))
        ) : (
          // 빈 데이터 안내
          <div className="py-12 text-center text-neutral-500">
            데이터가 없습니다
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default NewbieDetail;
